import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';

const { 
  FiZap, FiShield, FiDownload, FiSettings, FiImage, FiStar, 
  FiUsers, FiTrendingUp, FiLayers, FiMonitor, FiSmartphone, 
  FiGlobe, FiClock, FiCheckCircle, FiLock, FiHeart, FiTool, FiX 
} = FiIcons;

function Features() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const mainFeatures = [
    {
      icon: FiZap,
      title: "Lightning Fast Processing",
      description: "Advanced algorithms process your images in seconds, not minutes. Optimized for speed and efficiency.",
      benefits: ["Instant processing", "No waiting time", "Optimized algorithms", "Real-time preview"]
    },
    {
      icon: FiShield,
      title: "100% Secure & Private",
      description: "Your images are processed locally in your browser. They never leave your device or get stored on our servers.",
      benefits: ["Local processing", "No server uploads", "Complete privacy", "GDPR compliant"]
    },
    {
      icon: FiLayers,
      title: "Batch Processing",
      description: "Process hundreds of images simultaneously with our powerful batch tools. Perfect for bulk operations.",
      benefits: ["Up to 100 images", "Simultaneous processing", "Progress tracking", "ZIP downloads"]
    },
    {
      icon: FiSettings,
      title: "Advanced Customization",
      description: "Fine-tune every aspect of your image processing with professional-grade controls and settings.",
      benefits: ["Custom dimensions", "Quality control", "Format conversion", "Aspect ratio presets"]
    },
    {
      icon: FiDownload,
      title: "Multiple Download Options",
      description: "Download processed images individually or as ZIP archives. Choose what works best for your workflow.",
      benefits: ["Individual downloads", "ZIP archives", "Instant access", "No registration needed"]
    },
    {
      icon: FiStar,
      title: "Free Forever",
      description: "All features are completely free with no hidden costs, registration requirements, or usage limits.",
      benefits: ["No registration", "No watermarks", "Unlimited usage", "All features free"]
    }
  ];

  const toolFeatures = [
    {
      category: "Image Resizing",
      icon: FiImage,
      color: "blue",
      features: [
        "Custom pixel dimensions",
        "Percentage-based scaling",
        "Aspect ratio preservation",
        "Preset dimension options",
        "High-quality resampling",
        "Multiple format support"
      ]
    },
    {
      category: "Image Compression",
      icon: FiZap,
      color: "green",
      features: [
        "Up to 90% size reduction",
        "Quality level control",
        "Format optimization",
        "Visual quality preview",
        "Lossless PNG compression",
        "WebP format support"
      ]
    },
    {
      category: "Batch Processing",
      icon: FiLayers,
      color: "purple",
      features: [
        "Process up to 100 images",
        "Drag & drop interface",
        "Progress tracking",
        "Error handling",
        "Bulk settings application",
        "ZIP file downloads"
      ]
    }
  ];

  const technicalFeatures = [
    {
      icon: FiMonitor,
      title: "Cross-Platform Compatibility",
      description: "Works perfectly on all devices and operating systems"
    },
    {
      icon: FiSmartphone,
      title: "Mobile Optimized",
      description: "Fully responsive design optimized for mobile devices"
    },
    {
      icon: FiGlobe,
      title: "No Installation Required",
      description: "Browser-based tool that works instantly without downloads"
    },
    {
      icon: FiClock,
      title: "24/7 Availability",
      description: "Always available whenever you need to process images"
    },
    {
      icon: FiCheckCircle,
      title: "Quality Assurance",
      description: "Advanced algorithms maintain image quality during processing"
    },
    {
      icon: FiLock,
      title: "Data Protection",
      description: "Your images and data are never stored or transmitted"
    }
  ];

  const comparisonFeatures = [
    { feature: "Free to Use", us: true, others: "Limited" },
    { feature: "No Registration", us: true, others: false },
    { feature: "No Watermarks", us: true, others: false },
    { feature: "Batch Processing", us: true, others: "Premium Only" },
    { feature: "Local Processing", us: true, others: false },
    { feature: "Multiple Formats", us: true, others: "Limited" },
    { feature: "Custom Dimensions", us: true, others: "Basic Only" },
    { feature: "Quality Control", us: true, others: "Premium Only" },
    { feature: "ZIP Downloads", us: true, others: false },
    { feature: "Mobile Optimized", us: true, others: "Limited" }
  ];

  return (
    <>
      <SEOHead
        title="Features - Professional Image Processing Tools | ResizeImageFast"
        description="Discover all the powerful features of our free image processing tools. Resize, compress, batch process images with advanced settings, complete privacy, and professional quality results."
        keywords="image processing features, image resizer features, image compressor features, batch processing, free image tools, online image editor features, photo editing tools"
        url="/features"
        image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=630&fit=crop"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <div className="bg-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiTool} className="text-white text-3xl" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Powerful Features
                <span className="block text-blue-600">Built for Professionals</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Discover why thousands of users choose our image processing tools. 
                Professional-grade features, completely free, with privacy and security built-in.
              </p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto">
                {[
                  { number: "6+", label: "Core Features" },
                  { number: "100%", label: "Free Forever" },
                  { number: "4", label: "Processing Tools" },
                  { number: "0", label: "Hidden Costs" }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold text-blue-600">{stat.number}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Core Features That Make Us Different
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Professional-grade image processing with the simplicity and security you need
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mainFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
                >
                  <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={feature.icon} className="text-blue-600 text-xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-2 text-xs" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Tool-Specific Features */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Specialized Tool Features
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Each tool is packed with professional features designed for specific use cases
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-3 gap-8">
              {toolFeatures.map((tool, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white rounded-2xl p-8 shadow-lg"
                >
                  <div className={`bg-${tool.color}-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6`}>
                    <SafeIcon icon={tool.icon} className={`text-${tool.color}-600 text-2xl`} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {tool.category}
                  </h3>
                  <ul className="space-y-3">
                    {tool.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <SafeIcon icon={FiCheckCircle} className={`text-${tool.color}-500 mr-3`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Features */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Technical Excellence
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Built with modern web technologies for optimal performance and reliability
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {technicalFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center bg-gray-50 rounded-2xl p-6"
                >
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={feature.icon} className="text-blue-600 text-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How We Compare
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See why our tools offer more value than the competition
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-lg font-semibold text-gray-900">
                        Feature
                      </th>
                      <th className="px-6 py-4 text-center text-lg font-semibold text-blue-600">
                        ResizeImageFast
                      </th>
                      <th className="px-6 py-4 text-center text-lg font-semibold text-gray-600">
                        Other Tools
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {comparisonFeatures.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-gray-900 font-medium">
                          {item.feature}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {item.us === true ? (
                            <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl mx-auto" />
                          ) : (
                            <span className="text-blue-600 font-medium">{item.us}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {item.others === false ? (
                            <SafeIcon icon={FiX} className="text-red-500 text-xl mx-auto" />
                          ) : item.others === true ? (
                            <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl mx-auto" />
                          ) : (
                            <span className="text-gray-600 font-medium">{item.others}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-4">
                Experience All Features Free
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Try all our professional features right now - no registration, no limits, no cost!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <SafeIcon icon={FiImage} className="mr-2" />
                    Start Resizing Images
                  </motion.button>
                </Link>
                <Link to="/compress">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-800 transition-colors flex items-center"
                  >
                    <SafeIcon icon={FiZap} className="mr-2" />
                    Start Compressing Images
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Features;